/**
 * Promotion detail -- phase 6b, Task 3. `/discounts/promotions/:id`.
 *
 * Header: every promotion field read-only (GET /promotions/:id), the
 * stacking summary (ruling S-1: `stacksWithEffective` is what a customer
 * actually gets; own-side partners that do not reciprocate are called out
 * as a warning via `unreciprocated`), the code/redemption counts, and --
 * for MANUAL promotions, write role only -- Edit (Task 2's dialog) and
 * Activate/Deactivate. Below it two tabs: Codes (CodesTab) and Redemptions
 * (RedemptionsTab).
 *
 * Every write here invalidates the whole `['discounts']` prefix: the
 * detail's counts, the promotions list and the stats bar all change.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Link,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon, Edit as EditIcon, PauseCircle as PauseIcon, PlayCircle as PlayIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotify } from 'react-admin';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import { KIND_LABEL, discountLabel, fmtDateTime, managedOn } from '../format';
import { formatRand } from '../money';
import { useCanWriteDiscounts } from '../roles';
import type { PromotionDetail } from '../types';
import { PromotionDialog } from './PromotionDialog';
import { unreciprocated } from './stacking';
import { CodesTab } from './CodesTab';
import { RedemptionsTab } from './RedemptionsTab';

const limitLabel = (n: number | null): string => (n == null ? 'Unlimited' : String(n));
const idsLabel = (ids: string[] | null, noun: string): string => (ids == null ? `All ${noun}` : `${ids.length} ${noun}`);

type Field = { label: string; value: React.ReactNode };

const FieldList: React.FC<{ fields: Field[] }> = ({ fields }) => (
  <Box
    component="dl"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr', md: 'max-content 1fr max-content 1fr' },
      columnGap: 3,
      rowGap: 0.75,
      m: 0,
    }}
  >
    {fields.map((f) => (
      <React.Fragment key={f.label}>
        <Typography component="dt" variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {f.label}
        </Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0, minWidth: 0, overflowWrap: 'anywhere' }}>
          {f.value}
        </Typography>
      </React.Fragment>
    ))}
  </Box>
);

const stackingSummary = (p: PromotionDetail): React.ReactNode => {
  if (!p.stackable || p.stackingMode === 'none') return "Doesn't stack with other promotions";
  if (p.stackingMode === 'any') return 'Stacks with any other stackable promotion';
  const eff = p.stacksWithEffective.map((x) => x.name);
  return eff.length ? `Stacks with: ${eff.join(', ')}` : 'Stacks only with the listed partners — none reciprocate yet';
};

type TabKey = 'codes' | 'redemptions';

export const PromotionsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const notify = useNotify();
  const qc = useQueryClient();
  const canWrite = useCanWriteDiscounts();
  const [tab, setTab] = useState<TabKey>('codes');
  const [editOpen, setEditOpen] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(false);

  const detail = useQuery({
    queryKey: qk.promotion(id ?? ''),
    queryFn: () => discountsApi.getPromotion(id as string),
    enabled: Boolean(id),
  });

  const toggle = useMutation({
    mutationFn: (p: PromotionDetail) => discountsApi.setPromotionActive(p.id, !p.active),
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: qk.all });
      notify(saved.active ? `${saved.name} activated` : `${saved.name} deactivated`, { type: 'success' });
    },
    onError: (err) => notify(toApiFailure(err).message, { type: 'error' }),
    onSettled: () => setConfirmToggle(false),
  });

  if (!id) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        No promotion selected.
      </Typography>
    );
  }

  const back = (
    <Button component={RouterLink} to="/discounts/promotions" startIcon={<BackIcon />} size="small" sx={{ mb: 1 }}>
      All promotions
    </Button>
  );

  if (detail.isPending) {
    return (
      <Box>
        {back}
        <Skeleton variant="text" width={320} height={40} />
        <Skeleton variant="rectangular" height={160} sx={{ mt: 1 }} />
      </Box>
    );
  }

  if (detail.error || !detail.data) {
    const f = toApiFailure(detail.error);
    return (
      <Box>
        {back}
        <Alert severity="error">{f.status === 404 ? 'No such promotion.' : `Could not load this promotion: ${f.message}`}</Alert>
      </Box>
    );
  }

  const p = detail.data;
  const isManual = p.kind === 'MANUAL';
  const managed = managedOn(p.kind);
  const overstated = unreciprocated(p);

  const fields: Field[] = [
    { label: 'Discount', value: discountLabel(p) },
    { label: 'Banner', value: p.banner || '—' },
    { label: 'Min order', value: p.minOrderAmount ? formatRand(p.minOrderAmount) : 'None' },
    { label: 'Max discount', value: p.maxDiscountAmount ? formatRand(p.maxDiscountAmount) : 'Uncapped' },
    { label: 'Starts', value: fmtDateTime(p.startsAt) },
    { label: 'Ends', value: fmtDateTime(p.endsAt) },
    { label: 'Total limit', value: limitLabel(p.totalRedemptionLimit) },
    { label: 'Per customer', value: limitLabel(p.perCustomerLimit) },
    { label: 'Per customer / day', value: limitLabel(p.perCustomerDailyLimit) },
    { label: 'Priority', value: String(p.priority) },
    {
      label: 'Audience',
      value: p.firstOrderOnly ? 'First order only' : p.returningCustomerOnly ? 'Returning customers only' : 'Everyone',
    },
    { label: 'Products', value: idsLabel(p.eligibleProductIds, 'products') },
    { label: 'Categories', value: idsLabel(p.eligibleCategoryIds, 'categories') },
    ...(p.discountType === 'FREE_ITEM'
      ? [{ label: 'Free item', value: `${p.freeItemProductId ?? '—'} × ${p.freeItemQty ?? '—'}` }]
      : []),
    { label: 'Stacking', value: stackingSummary(p) },
    { label: 'Created', value: fmtDateTime(p.createdAt) },
    { label: 'Updated', value: fmtDateTime(p.updatedAt) },
  ];

  return (
    <Box>
      {back}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h6" component="h2" fontWeight={800} sx={{ overflowWrap: 'anywhere' }}>
              {p.name}
            </Typography>
            <Chip label={KIND_LABEL[p.kind] ?? p.kind} size="small" />
            <Chip label={p.active ? 'Active' : 'Inactive'} size="small" color={p.active ? 'success' : 'default'} />
            {managed && <Chip label={managed} size="small" variant="outlined" color="info" />}
          </Stack>
          <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Codes (active / total):{' '}
              <Typography component="span" variant="body2" fontWeight={700} color="text.primary">
                {p.activeVoucherCount} / {p.voucherCount}
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Confirmed redemptions:{' '}
              <Typography component="span" variant="body2" fontWeight={700} color="text.primary">
                {p.confirmedRedemptionCount}
              </Typography>
            </Typography>
          </Stack>
        </Box>
        {canWrite && isManual && (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)} aria-label={`Edit ${p.name}`}>
              Edit
            </Button>
            <Tooltip title={p.active ? 'Customers can no longer redeem its codes' : 'Its codes become redeemable again'}>
              <Button
                variant="outlined"
                color={p.active ? 'warning' : 'success'}
                startIcon={p.active ? <PauseIcon /> : <PlayIcon />}
                onClick={() => setConfirmToggle(true)}
                aria-label={p.active ? `Deactivate ${p.name}` : `Activate ${p.name}`}
              >
                {p.active ? 'Deactivate' : 'Activate'}
              </Button>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      {managed && !isManual && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {p.kind === 'LOYALTY_REWARD'
            ? 'This is a seeded loyalty promotion. Its amount and bands are edited on the '
            : p.kind === 'REFERRAL' || p.kind === 'REFERRAL_REWARD'
              ? 'This is a seeded referral promotion. Its amounts are edited on the '
              : 'This promotion is read-only here. '}
          {p.kind === 'LOYALTY_REWARD' && (
            <Link component={RouterLink} to="/discounts/loyalty">
              Loyalty tab
            </Link>
          )}
          {(p.kind === 'REFERRAL' || p.kind === 'REFERRAL_REWARD') && (
            <Link component={RouterLink} to="/discounts/referrals">
              Referrals tab
            </Link>
          )}
          {p.kind !== 'AUTO_CART' && '. Its codes are issued by the engine, but they can still be assigned, expired or deactivated below.'}
        </Alert>
      )}

      {overstated.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Stacking is overstated: {overstated.join(', ')} {overstated.length === 1 ? 'does' : 'do'} not allow this promotion back, so
          customers will not get both. Only the partners listed under “Stacking” actually stack.
        </Alert>
      )}

      <FieldList fields={fields} />

      <Divider sx={{ my: 2 }} />

      <Tabs value={tab} onChange={(_e, next: TabKey) => setTab(next)} aria-label="Promotion sections" sx={{ mb: 2 }}>
        <Tab value="codes" label="Codes" id="promotion-tab-codes" />
        <Tab value="redemptions" label="Redemptions" id="promotion-tab-redemptions" />
      </Tabs>

      {tab === 'codes' ? <CodesTab promotion={p} /> : <RedemptionsTab promotionId={p.id} />}

      {canWrite && isManual && (
        <>
          <PromotionDialog open={editOpen} initial={p} onClose={() => setEditOpen(false)} />
          <Dialog open={confirmToggle} onClose={toggle.isPending ? undefined : () => setConfirmToggle(false)}>
            <DialogTitle>{p.active ? `Deactivate ${p.name}?` : `Activate ${p.name}?`}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {p.active
                  ? 'Customers will no longer be able to redeem its codes until it is activated again.'
                  : 'Its codes become redeemable again (subject to the dates and limits).'}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmToggle(false)} disabled={toggle.isPending}>
                Cancel
              </Button>
              <Button variant="contained" disabled={toggle.isPending} onClick={() => toggle.mutate(p)}>
                {p.active ? 'Deactivate' : 'Activate'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default PromotionsDetail;
